import React, { useState, useRef, useEffect } from 'react';
import Styles from './BrandCard.module.scss';
import { FiTwitter, FiFileText, FiTrendingUp, FiChevronRight, FiMoreVertical, FiEdit2, FiTrash2, FiBarChart2, FiUsers } from 'react-icons/fi';

const BrandCard = ({ brand, onClick, onEdit, onDelete, onViewInsights, onShare }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onEdit?.(brand);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onDelete?.(brand);
  };

  const handleViewInsights = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onViewInsights?.(brand);
  };

  const handleShare = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onShare?.(brand);
  };

  // Calculate completion percentage
  const calculateCompletion = () => {
    let completed = 0;
    let total = 7; // Total sections in Brand Intelligence

    if (brand.brand_name) completed++;
    if (brand.url || brand.description) completed++;
    if (brand.industries?.length > 0) completed++;
    if (brand.target_audience?.length > 0) completed++;
    if (brand.content_samples_count > 0) completed++;
    if (brand.social_channels_count > 0) completed++;
    if (brand.content_topics?.length > 0) completed++;

    return Math.round((completed / total) * 100);
  };

  const completion = calculateCompletion();

  // Get completion status color
  const getCompletionColor = () => {
    if (completion >= 80) return 'teal';
    if (completion >= 50) return 'coral';
    return 'lavender';
  };

  const completionColor = getCompletionColor();

  return (
    <div className={Styles.card} onClick={onClick}>
      {/* Gradient Border Effect */}
      <div className={`${Styles.cardBorder} ${Styles[`border${completionColor.charAt(0).toUpperCase() + completionColor.slice(1)}`]}`}></div>

      {/* Card Content */}
      <div className={Styles.cardContent}>
        {/* Header */}
        <div className={Styles.header}>
          <div className={Styles.headerLeft}>
            {brand.logo ? (
              <img src={brand.logo} alt={brand.brand_name} className={Styles.logo} />
            ) : (
              <div className={Styles.logoPlaceholder}>
                {brand.brand_name?.charAt(0) || 'B'}
              </div>
            )}
            <div>
              <h3 className={Styles.brandName}>{brand.brand_name || 'Unnamed Brand'}</h3>
              <p className={Styles.brandIndustry}>
                {brand.industries?.[0] || 'No industry set'}
              </p>
            </div>
          </div>
          <div className={Styles.headerActions}>
            <div className={Styles.menuContainer} ref={menuRef}>
              <button className={Styles.menuButton} onClick={handleMenuClick}>
                <FiMoreVertical size={18} />
              </button>
              {showMenu && (
                <div className={Styles.dropdownMenu}>
                  <button className={Styles.menuItem} onClick={handleEdit}>
                    <FiEdit2 size={14} />
                    <span>Edit Brand</span>
                  </button>
                  <button className={Styles.menuItem} onClick={handleViewInsights}>
                    <FiBarChart2 size={14} />
                    <span>View Insights</span>
                  </button>
                  <button className={Styles.menuItem} onClick={handleShare}>
                    <FiUsers size={14} />
                    <span>Share Brand</span>
                  </button>
                  <button className={`${Styles.menuItem} ${Styles.menuItemDanger}`} onClick={handleDelete}>
                    <FiTrash2 size={14} />
                    <span>Delete Brand</span>
                  </button>
                </div>
              )}
            </div>
            <button className={Styles.viewButton}>
              <FiChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className={Styles.statsRow}>
          <div className={`${Styles.stat} ${Styles.statTeal}`}>
            <FiFileText size={16} className={Styles.statIcon} />
            <span className={Styles.statValue}>{brand.content_samples_count || 0}</span>
            <span className={Styles.statLabel}>Samples</span>
          </div>
          <div className={`${Styles.stat} ${Styles.statCoral}`}>
            <FiTwitter size={16} className={Styles.statIcon} />
            <span className={Styles.statValue}>{brand.social_channels_count || 0}</span>
            <span className={Styles.statLabel}>Channels</span>
          </div>
          <div className={`${Styles.stat} ${Styles.statLavender}`}>
            <FiTrendingUp size={16} className={Styles.statIcon} />
            <span className={Styles.statValue}>{brand.content_topics?.length || 0}</span>
            <span className={Styles.statLabel}>Topics</span>
          </div>
        </div>

        {/* Completion Bar */}
        <div className={Styles.completionSection}>
          <div className={Styles.completionHeader}>
            <span className={Styles.completionLabel}>Brand Training</span>
            <span className={Styles.completionPercentage}>{completion}%</span>
          </div>
          <div className={Styles.completionBar}>
            <div
              className={`${Styles.completionFill} ${Styles[`fill${completionColor.charAt(0).toUpperCase() + completionColor.slice(1)}`]}`}
              style={{ width: `${completion}%` }}
            ></div>
          </div>
        </div>

        {/* Quick Info Tags */}
        {brand.target_audience && brand.target_audience.length > 0 && (
          <div className={Styles.tags}>
            {brand.target_audience.slice(0, 2).map((audience, idx) => (
              <span key={idx} className={Styles.tag}>{audience}</span>
            ))}
            {brand.target_audience.length > 2 && (
              <span className={Styles.tagMore}>+{brand.target_audience.length - 2}</span>
            )}
          </div>
        )}

        {/* Updated Date */}
        {brand.updated_at && (
          <div className={Styles.updatedDate}>
            Last updated {new Date(brand.updated_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandCard;
